import moment from 'moment';
import React, { useMemo } from 'react';
import {
    Button, Table, Title,
} from 'ui-library';
import { useFormatting } from 'locale';
import { useOptionList } from 'hooks/useOptionList';
import { OPTION_LIST } from 'constants/constants';
import { dateFormatZurich } from 'utils/datetime';
import {
    nextReallocationColumnDefs,
} from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/pages/Strategy/columnDefs';
import { WithdrawalStatus } from 'core/types/api/Api';
import { roundToNearestTenth } from 'utils/formatting';
import RiskScale from '../../../../components/RiskScale/RiskScale';
import { InvestmentStrategyAllocations } from '../../../../components/InvestmentStrategyAllocations';

import './Strategy.css';

const BasicStrategy = ({
    onChangePlan,
    allocations,
    portfolioProducts,
    t,
    modelPortfolioDescription,
    riskCategories,
    strategy,
    getFormattedXAxisFormat,
    isPortfolioChangeable,
    ongoingPlan,
    riskName,
    withdrawalData,
    isAZP,
    isZivv,
    hasAccessToChangeStrategy,
}) => {
    const { getAmountWithDecimals, getFormattedDate } = useFormatting();
    const {
        lists: [optionWithdrawalPeriodicity],
    } = useOptionList([OPTION_LIST.WithdrawalPeriodicity]);

    const frequency = useMemo(() => optionWithdrawalPeriodicity?.find(f => f.id === withdrawalData?.frequency)?.label, [withdrawalData, optionWithdrawalPeriodicity]);

    const reallocationColumns = useMemo(() => nextReallocationColumnDefs(t, getFormattedDate), [t, getFormattedDate]);

    const getWithdrawalStatus = ({ status } : { status: WithdrawalStatus}) => {
        switch (status) {
            case 1:
                return t('portfolio.status.active');
            case 2:
                return t('portfolio.status.paused');
            default:
                return null;
        }
    };

    return (
        <>
            <div className="col-xs-12 col-lg-6">
                <div className="fi-verticals">
                    <dl className="Strategy__Summary">
                        <div>
                            <dt className="Strategy__Summary__Key">{t('portfolioDetail.strategy.chosenInvestmentRisk')}</dt>
                            <dd className="Strategy__Summary__Value">
                                <div>{riskName}</div>
                            </dd>
                        </div>
                        <div>
                            <dt className="Strategy__Summary__Key">{t('portfolioDetail.strategy.chosenInvestmentStrategy')}</dt>
                            <dd className="Strategy__Summary__Value">
                                {
                                    portfolioProducts.map((product) => (
                                        <>
                                            <div>{product?.name}</div>
                                            {!!product?.factsheetUrl && (
                                                <div>
                                                    <a
                                                        href={product?.factsheetUrl}
                                                        target="_blank"
                                                        className="button"
                                                        rel="noreferrer"
                                                    >
                                                        {t('portfolio.createNewPortfolio.strategy.factsheetUrl')}
                                                    </a>
                                                </div>
                                            )}
                                        </>
                                    ))
                                }

                            </dd>
                        </div>
                    </dl>
                    <div>
                        <Title type={3}>
                            {t('portfolioDetail.strategy.modelPortfolioDetails')}
                        </Title>
                        <p>
                            {modelPortfolioDescription}
                        </p>
                    </div>
                    <div>
                        <dt className="Strategy__Summary__Key">{t('portfolio.createNewPortfolio.risk')}</dt>
                        <dd className="Strategy__Summary__Value">
                            <div>
                                <RiskScale scales={riskCategories} activeIndex={strategy}/>
                            </div>
                        </dd>
                    </div>
                    {withdrawalData?.bankIban && (
                        <dl className="Strategy__Summary withdrawal">
                            <div>
                                <Title type={3}>
                                    {t('portfolio.createNewPortfolio.withdrawalPlan')}
                                </Title>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">IBAN</dt>
                                <dd className="Strategy__Summary__Value">
                                    {withdrawalData?.bankIban}
                                </dd>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">{t('3b.withdrawalPlan.amount')}</dt>
                                <dd className="Strategy__Summary__Value">
                                    <strong>{withdrawalData?.withdrawalAmount ? getAmountWithDecimals(roundToNearestTenth(withdrawalData?.withdrawalAmount)) : '-'}</strong>
                                </dd>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">{t('productSummary.frequency')}</dt>
                                <dd className="Strategy__Summary__Value text-capitalize">{frequency || '-'}</dd>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">{t('portfolio.transferPlans.nextTransfer')}</dt>
                                <dd className="Strategy__Summary__Value">{withdrawalData?.nextExecutionDate ? moment(withdrawalData?.nextExecutionDate).format(dateFormatZurich) : '-'}</dd>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">{t('portfolio.transferPlans.lastExecutionDate')}</dt>
                                <dd className="Strategy__Summary__Value">{withdrawalData?.lastExecutionDate ? moment(withdrawalData?.lastExecutionDate).format(dateFormatZurich) : '-'}</dd>
                            </div>
                            <div>
                                <dt className="Strategy__Summary__Key">{t('portfolios.columns.status')}</dt>
                                <dd className="Strategy__Summary__Value">{getWithdrawalStatus({ status: withdrawalData?.status }) || '-'}</dd>
                            </div>
                            {withdrawalData?.plannedEndDate && (
                                <>
                                    <div>
                                        <dt className="Strategy__Summary__Key">
                                            {t('portfolio.transferPlans.plannedEndDate')}
                                            *
                                        </dt>
                                        <dd className="Strategy__Summary__Value">{moment(withdrawalData?.plannedEndDate).format('MM.yyyy')}</dd>
                                    </div>
                                    <div className="planned-end-date-info">
                                        *
                                        {' '}
                                        {t('portfolio.transferPlans.plannedEndDate.info')}
                                    </div>
                                </>
                            )}
                        </dl>
                    )}
                </div>
            </div>
            <div className="col-xs-12 col-lg-6">
                <div className="fi-verticals Strategy__Description">
                    <div className="Strategy__Chart">
                        <InvestmentStrategyAllocations
                            allocations={allocations}
                            getFormattedXAxisFormat={getFormattedXAxisFormat}
                        />
                    </div>
                    {isPortfolioChangeable && (
                        <div>
                            <Title type={3}>
                                {t('portfolioDetail.strategy.changeStrategyTitle')}
                            </Title>
                            {!isAZP && hasAccessToChangeStrategy && (
                                <p>
                                    {t('portfolioDetail.strategy.changeStrategyText')}
                                </p>
                            )}
                            {!hasAccessToChangeStrategy && (
                                <p>
                                    {t('portfolioDetail.strategy.changeStrategyTextForNonSwiss')}
                                </p>
                            )}
                            <div className={`button-group${isAZP ? ' mt-20' : ''}`}>
                                <Button
                                    data-cy="changePlanBtn"
                                    disabled={ongoingPlan || !hasAccessToChangeStrategy}
                                    onClick={onChangePlan}
                                >
                                    {(isZivv || isAZP) ? t('portfolioDetail.strategy.changeInvestmentStrategy') : t('portfolioDetail.strategy.changePlanBtn')}
                                </Button>
                            </div>
                        </div>
                    )}
                    {!!withdrawalData?.nextReallocations?.length && (
                        <div>
                            <dt className="Strategy__Summary__Key">{t('portfolio.createNewPortfolio.nextReallocation')}</dt>
                            <Table columns={reallocationColumns} data={withdrawalData.nextReallocations} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BasicStrategy;
